import { createAsyncThunk } from '@reduxjs/toolkit';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import teamsResource from 'modules/teams/domain/resources/teams-resource';
import { TEAMS_LIST_TAG } from 'modules/teams/store/teams-api';
import { baseApi } from 'shared/api/base-api';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';

const PREFIX = 'teams';

/** Сбрасывает кэш карточки команды — мутации здесь thunk'и, тег сам не инвалидируется. */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

/**
 * Сбрасывает перечень команд. Отдельно от `invalidateTeam`: тег с `id` и тег
 * списка в RTK Query не пересекаются, поэтому появление и удаление команды надо
 * объявлять именно тегом списка.
 */
const invalidateTeamsList = () => baseApi.util.invalidateTags([TEAMS_LIST_TAG]);

export const createTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(
  `${PREFIX}/createTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const team = { name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      const teamId = teamData.id;

      if (payload.logo) {
        const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
        const res = await teamsResource.uploadLogo(logo, teamId);
        teamData.logo = { id: res.id, url: res.emblem };
      }

      teamData.createdAt = getNormalizedDate(new Date());
      dispatch(invalidateTeamsList());

      return teamData;
    } catch (error) {
      console.error(`${PREFIX}/createTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const deleteTeam = createAsyncThunk<{ id: number }, { id: number }>(
  `${PREFIX}/deleteTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await teamsRepository.deleteTeam(payload.id);
      dispatch(invalidateTeamsList());
      return { id: payload.id };
    } catch (error) {
      console.error(`${PREFIX}/deleteTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const upsertTeamKits = createAsyncThunk<
  Team,
  { teamId: number; kits: Array<{ type: string; elements: Array<{ element: string; pattern: string; colors: string[] }> }> }
>(`${PREFIX}/upsertTeamKits`, async (payload, { rejectWithValue, dispatch }) => {
  try {
    const team = await teamsRepository.upsertTeamKits(payload.teamId, payload.kits);
    dispatch(invalidateTeam(payload.teamId));

    return team;
  } catch (error) {
    console.error(`${PREFIX}/upsertTeamKits error:`, error);
    const message = error instanceof Error ? error.message : String(error);
    return rejectWithValue(message);
  }
});

export const editTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(
  `${PREFIX}/editTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const team = { id: payload.id, name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      const teamId = teamData.id;

      if (payload.logo instanceof File) {
        const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
        const res = await teamsResource.uploadLogo(logo, teamId);
        teamData.logo = { id: res.id, url: res.emblem };
      }

      dispatch(invalidateTeam(teamId));

      return teamData;
    } catch (error) {
      console.error(`${PREFIX}/editTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);
